import { Floorplan } from "@domain/project/floorplan/floorplan";
import { FloorplanEventType } from "@domain/project/floorplan/floorplan-event";
import { FloorplanItem } from "@domain/project/floorplan/floorplan-item";
import { ProjectImageMetadata } from "@domain/project/project-image-metadata";
import { Exclude, Expose } from "class-transformer";
import { IsNumber, IsPositive, IsString } from "class-validator";
import { v4 as uuidv4 } from "uuid";

export class FloorplanImage extends FloorplanItem {
  @Exclude()
  private _projectImage: ProjectImageMetadata;

  @IsString()
  @Expose({ name: "projectImageId" })
  private readonly _projectImageId: string;

  @IsNumber()
  @IsPositive({ message: "The value must be higher than 0" })
  @Expose({ name: "size" })
  private _size: number;

  constructor(
    floorplan: Floorplan,
    projectImage: ProjectImageMetadata,
    id: string,
    x: number,
    y: number,
    size: number,
    notes?: string,
  ) {
    super(floorplan, id, x, y, notes);
    this._projectImageId = projectImage?.id;
    this._projectImage = projectImage;
    this._size = size;
  }

  get size(): number {
    return this._size;
  }

  updateSize(value: number, x: number, y: number) {
    this._size = value;
    this._floorplan.publishUpdate(FloorplanEventType.ITEM_SIZE_CHANGED, this);
    this.updatePosition(x, y);
  }

  setItemTextVisibility() {
    this.itemTextVisible = this._floorplan.floorplanState.imagesItemTextVisible;
  }

  get projectImage(): ProjectImageMetadata {
    return this._projectImage;
  }

  get projectImageId(): string {
    return this._projectImageId;
  }

  protected override createRawDuplicate(): ((x: number, y: number) => FloorplanItem) | undefined {
    const projectImage = this.projectImage;
    const size = this.size;

    return (x: number, y: number) => FloorplanImage.create(this._floorplan, projectImage, x, y, size);
  }

  override delete() {
    this._floorplan.deleteImage(this);
  }

  override getDuplicateTargetCollection() {
    return this._floorplan.images;
  }

  protected override registerDuplicate() {
    return (duplicate: FloorplanItem, afterDuplicateAdded?: (duplicate: FloorplanItem) => void) =>
      this.registerDuplicateInFloorplan(
        duplicate as FloorplanImage,
        this._floorplan.images,
        FloorplanEventType.IMAGE_ADDED,
        () => this._floorplan.project.setImagePositionIds(),
        afterDuplicateAdded as ((duplicate: FloorplanImage) => void) | undefined,
      );
  }

  protected override refreshFloorplanLockState() {
    this._floorplan.floorplanState.refreshImagesLock();
  }

  static create(floorplan: Floorplan, projectImage: ProjectImageMetadata, x: number, y: number, size: number): FloorplanImage {
    return new FloorplanImage(floorplan, projectImage, uuidv4(), x, y, size);
  }
}
